import React, { useEffect, useRef, useState } from 'react';
import { CalendarClock, Loader2, X } from 'lucide-react';
import { apiService } from '../../services/api';

let upcoming = null; // prochaines réunions, chargées une fois par page
const loadUpcoming = () => {
  if (!upcoming) {
    upcoming = apiService.get('/talking-points/meetings?days=21').catch((err) => {
      upcoming = null;
      throw err;
    });
  }
  return upcoming;
};

export const meetingLabel = (point) => {
  if (!point.event_start) return point.event_title || 'Réunion';
  const date = new Date(point.event_start);
  const when = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  return `${point.event_title || 'Réunion'}${when ? ` · ${when}` : ''}`;
};

// Point rattaché à une réunion précise de l'agenda (badge, détacher), ou bouton pour l'y rattacher.
const AttachMeeting = ({ point, onChange, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [meetings, setMeetings] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    loadUpcoming().then((data) => {
      if (!data.connected) setError('Relie ton agenda Google (Mes réglages) pour voir tes réunions.');
      setMeetings(data.meetings || []);
    }).catch((err) => setError(err.message));
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const save = async (fields) => {
    try {
      onChange(await apiService.patch(`/talking-points/${point.id}`, fields));
      setOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (point.event_id) {
    return (
      <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
        <CalendarClock size={11} className="shrink-0" />
        <span className="truncate">{meetingLabel(point)}</span>
        <button type="button" title="Détacher de cette réunion" onClick={() => save({ event_id: null })} className="rounded-full p-0.5 hover:bg-violet-100">
          <X size={10} />
        </button>
      </span>
    );
  }

  return (
    <span ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)} title="Rattacher à une de mes prochaines réunions"
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-violet-700 ${compact ? 'opacity-0 group-hover:opacity-100' : ''}`}>
        <CalendarClock size={11} /> Pour une réunion
      </button>
      {open && (
        <span className="absolute right-0 top-full z-30 mt-1 block w-72 rounded-xl border border-slate-200 bg-white p-1.5 text-left shadow-lg">
          {error ? <span className="block px-2 py-1.5 text-xs text-rose-600">{error}</span>
            : meetings === null ? <span className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> Agenda…</span>
              : meetings.length === 0 ? <span className="block px-2 py-1.5 text-xs text-slate-400">Aucune réunion ces trois prochaines semaines.</span>
                : (
                  <span className="block max-h-64 overflow-y-auto">
                    {meetings.map((m) => (
                      <button key={m.event_id} type="button"
                        onClick={() => save({ event_id: m.event_id, event_title: m.title, event_start: m.start })}
                        className="block w-full rounded-lg px-2 py-1.5 text-left text-xs hover:bg-violet-50">
                        <span className="block truncate font-medium text-slate-800">{m.title}</span>
                        <span className="block truncate text-[11px] text-slate-500">
                          {new Date(m.start).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} {m.time}
                          {(m.project_name || m.client_name) ? ` · ${m.project_name || m.client_name}` : ''}
                        </span>
                      </button>
                    ))}
                  </span>
                )}
        </span>
      )}
    </span>
  );
};

export default AttachMeeting;
